import { collection } from 'infra/collection'
import { CursorPaged, CursorPageQuery, Id } from 'types/definitions'
import { SearchableUserDTO } from './dtos/searchable-user'
import { SearchableUser } from './models/SearchableUser'
import { UserDataType } from './models/UserDataType'
import { IAMAPI } from './API'
import { PrimeFullMember } from './MemberStore'
import { PrimeFullWorkspace } from './WorkspaceStore'
import { RefreshOnlineStatusStream } from './RefreshOnlineStatusStream'
import { beingObservedStream } from 'utils/mobx-util'
import { distinctUntilChanged, Observable, startWith, switchMap } from 'rxjs'
import { action } from 'mobx'

export interface GetUserParams {
  memberships?: boolean
}

export interface FetchUsersQuery extends CursorPageQuery {
  limit?: number
  search_text?: string
  after?: string | null
  user_types?: UserDataType[] | null
  workspace_id?: string
  inactive?: boolean | null
  role_id?: string
}

export default class SearchableUserStore {
  searchableUsers = collection<SearchableUserDTO, SearchableUser>({
    create: () => {
      const user = new SearchableUser(
        this.primeFullMember,
        this.primeFullWorkspace,
      )
      this.subscribeToOnlineStatus(user)
      return user
    },
    getDataId: (data) => data.public_id,
    getModelId: (model) => model.publicId,
  })

  constructor(
    private api: IAMAPI,
    private primeFullMember: PrimeFullMember,
    private primeFullWorkspace: PrimeFullWorkspace,
    private getStatusChangeStreamForUser: (userId: Id) => Observable<any>,
    private fetchOnlineStatus: (userId: Id) => Promise<boolean>,
  ) {}

  async getUser(
    publicId: string,
    params: GetUserParams,
  ): Promise<SearchableUser> {
    const user = await this.api.userSearch.get(publicId, params)

    return this.searchableUsers.set(user)
  }

  async fetchUsers(
    query: FetchUsersQuery,
  ): Promise<CursorPaged<SearchableUser>> {
    const results = await this.api.userSearch.find(query)

    return {
      ...results,
      data: this.searchableUsers.set(results.data),
    }
  }

  private subscribeToOnlineStatus(searchableUser: SearchableUser) {
    RefreshOnlineStatusStream(
      beingObservedStream(searchableUser, 'online'),
      this.getStatusChangeStreamForUser(searchableUser.publicId),
    )
      .pipe(
        switchMap(async () => this.fetchOnlineStatus(searchableUser.publicId)),
        startWith(false),
        distinctUntilChanged(),
      )
      .subscribe(
        action((online: boolean) => {
          searchableUser.online = online
        }),
      )
  }
}
